import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const PAGE_SIZE = 20;

// Чип оплаты в списке заказов (цвета как у STATUS_BADGE в деталке)
const PAY_CHIP = {
  paid:    { label: 'Paid',    bg: '#dcfce7', color: '#166534', border: '#86efac' },
  partial: { label: 'Partial', bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
  overdue: { label: 'Overdue', bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' },
  unpaid:  { label: 'Unpaid',  bg: '#f3f4f6', color: '#374151', border: '#cbd5e1' },
};

/**
 * MaterialOrdersList
 *
 * Renders a paginated list of orders for a given material type.
 * Props:
 *   tabKey        — 'master-bags' | 'small-bags' | 'seeds' | 'mini-tools' | 'desiccant-bags'
 *   typeLabel     — human label for the tab (e.g. "Master Bags")
 *   materialtypeID — number (null for seeds tab)
 *   search        — string
 *   selectedBrands — string[]
 *   selectedNiches — string[]
 *   onSelectOrder — (orderID) => void
 */
const MaterialOrdersList = ({
  tabKey,
  typeLabel,
  materialtypeID,
  search,
  selectedBrands,
  selectedNiches,
  selectedStatuses = [],
  unpaidOnly = false,
  onSelectOrder,
}) => {
  const [orders, setOrders]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [totalQty, setTotalQty]   = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  // Комментарии заказов: { [orderID]: comment }; origComments — для сравнения при blur
  const [comments, setComments]   = useState({});
  const origComments              = useRef({});

  // Бейджи заказов: { [orderID]: { attachments, payment_status, payment_due_date } }
  const [badges, setBadges]       = useState({});

  const isSeeds         = tabKey === 'seeds';
  const isFertilizer    = tabKey === 'fertilizer-orders';
  const isReadyProducts = tabKey === 'ready-products';

  // Защита от гонки: применяем только ответ самого свежего запроса
  // (быстрый двойной клик по фильтру Unpaid иначе оставляет список от старого)
  const fetchSeq = useRef(0);

  const fetchOrders = useCallback(async (currentPage) => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    setError(null);
    try {
      const params = { page: currentPage, limit: PAGE_SIZE, search };
      if (selectedStatuses.length > 0) params.statusIDs = selectedStatuses.join(',');
      if (unpaidOnly) { params.unpaidOnly = '1'; params.orderType = tabKey; }

      let url;
      if (isFertilizer) {
        url = '/api/fert-orders-list';
      } else if (isSeeds) {
        url = '/api/material-orders/seeds-list';
      } else if (isReadyProducts) {
        url = '/api/material-orders/ready-products-list';
      } else {
        url = '/api/material-orders/list';
        params.materialtypeID = materialtypeID;
        if (selectedBrands.length > 0) params.brandIDs = selectedBrands.join(',');
        if (selectedNiches.length > 0) params.nicheIDs = selectedNiches.join(',');
      }

      const { data } = await api.get(url, { params });
      if (seq !== fetchSeq.current) return;
      setOrders(data.orders || []);
      setTotal(data.total || 0);
      setTotalQty(data.total_qty || 0);
      setTotalAmount(data.total_amount || 0);
      // Сразу чистим карты прошлой выборки: ключи — голые orderID, и синтетические
      // period-YYYY-MM пересекаются между вкладками (чужой Paid-чип до ответа бейджей)
      setComments({});
      origComments.current = {};
      setBadges({});

      // Подтягиваем комментарии и бейджи (вложения/оплата) для видимых заказов
      const ids = (data.orders || []).map(o => o.orderID).filter(Boolean);
      if (ids.length > 0) {
        const metaParams = { orderType: tabKey, orderIDs: ids.join(',') };
        try {
          const { data: cm } = await api.get('/api/material-orders/comments', { params: metaParams });
          if (seq !== fetchSeq.current) return;
          setComments(cm || {});
          origComments.current = cm || {};
        } catch { /* карты уже сброшены выше */ }
        try {
          const { data: bd } = await api.get('/api/material-orders/order-badges', { params: metaParams });
          if (seq !== fetchSeq.current) return;
          setBadges(bd || {});
        } catch { /* карты уже сброшены выше */ }
      }
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      console.error('Error fetching orders list:', err);
      setError('Failed to load orders');
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [tabKey, isSeeds, isFertilizer, isReadyProducts, materialtypeID, search, selectedBrands, selectedNiches, selectedStatuses, unpaidOnly]);

  const saveComment = async (orderID) => {
    const value = comments[orderID] ?? '';
    if (value === (origComments.current[orderID] ?? '')) return;
    try {
      await api.put('/api/material-orders/comment', {
        orderID, orderType: tabKey, comment: value,
      });
      origComments.current = { ...origComments.current, [orderID]: value };
    } catch (err) {
      console.error('Error saving comment:', err);
    }
  };

  // Reset to page 1 when filters/search change
  useEffect(() => {
    setPage(1);
  }, [tabKey, search, selectedBrands, selectedNiches, selectedStatuses, unpaidOnly]);

  useEffect(() => {
    fetchOrders(page);
  }, [fetchOrders, page]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const delta = 2;
    const left  = Math.max(1, page - delta);
    const right = Math.min(totalPages, page + delta);

    if (left > 1) {
      pages.push(1);
      if (left > 2) pages.push('...');
    }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) {
      if (right < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }

    return (
      <div className="mo-pagination">
        <button
          className="mo-page-btn"
          onClick={() => setPage(p => p - 1)}
          disabled={page === 1}
        >
          <i className="fas fa-chevron-left"></i>
        </button>

        {pages.map((p, i) =>
          p === '...' ? (
            <span key={`dots-${i}`} className="mo-page-info">…</span>
          ) : (
            <button
              key={p}
              className={`mo-page-btn${page === p ? ' active' : ''}`}
              onClick={() => setPage(p)}
            >
              {p}
            </button>
          )
        )}

        <button
          className="mo-page-btn"
          onClick={() => setPage(p => p + 1)}
          disabled={page === totalPages}
        >
          <i className="fas fa-chevron-right"></i>
        </button>

        <span className="mo-page-info">
          {total} order{total !== 1 ? 's' : ''}
        </span>
      </div>
    );
  };

  const renderStatuses = (order) => {
    const chips = [...new Set(String(order.statuses || '').split(',').filter(Boolean))];
    const b = badges[order.orderID];
    let pay = null;
    if (b?.payment_status) {
      const overdue = b.payment_status !== 'paid' && b.payment_due_date &&
        b.payment_due_date < new Date().toLocaleDateString('en-CA');
      pay = overdue ? PAY_CHIP.overdue : PAY_CHIP[b.payment_status];
    } else if (unpaidOnly) {
      // При активном фильтре помечаем и заказы без записи об оплате —
      // иначе результат фильтра выглядит как список без единого чипа Unpaid
      pay = PAY_CHIP.unpaid;
    }
    if (chips.length === 0 && !pay && !(b?.attachments > 0)) return null;
    return (
      <div className="mo-order-statuses" style={{ alignItems: 'center' }}>
        {chips.map(s => (
          <span key={s} className={`mo-status-chip ${s}`}>{s}</span>
        ))}
        {b?.attachments > 0 && (
          <i
            className="fas fa-paperclip"
            title={`${b.attachments} document${b.attachments !== 1 ? 's' : ''}`}
            style={{ color: '#64748b', fontSize: 12 }}
          ></i>
        )}
        {pay && (
          <span className="mo-status-chip" style={{ background: pay.bg, color: pay.color, borderColor: pay.border }}>
            {pay.label}
          </span>
        )}
      </div>
    );
  };

  if (loading && orders.length === 0) {
    return (
      <div className="mo-loading">
        <i className="fas fa-spinner fa-spin"></i> Loading orders...
      </div>
    );
  }

  if (error) {
    return <div className="mo-empty">{error}</div>;
  }

  if (!loading && orders.length === 0) {
    return <div className="mo-empty">No orders found</div>;
  }

  return (
    <div>
      {loading && (
        <div className="mo-loading" style={{ padding: '12px', justifyContent: 'flex-end' }}>
          <i className="fas fa-spinner fa-spin"></i>
        </div>
      )}

      <div className="mo-list">
        {orders.map(order => (
          <div
            key={order.orderID}
            className="mo-order-card"
            onClick={() => onSelectOrder(order.orderID)}
          >
            <div className="mo-order-date">
              {MONTH_NAMES[(order.month_num || 1) - 1]} {order.year}
            </div>
            <div className="mo-order-type">{typeLabel}</div>
            {order.suppliers && (
              <div className="mo-order-suppliers">{order.suppliers}</div>
            )}
            <div className="mo-order-id">
              {order.orderID}
            </div>
            <div className="mo-order-items">
              {order.item_count} item{order.item_count !== 1 ? 's' : ''}
            </div>
            <div className="mo-order-items">
              {Number(order.total_qty || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}
              {' '}{(tabKey === 'seeds' || tabKey === 'fertilizer-orders') ? 'lb' : 'pcs'}
            </div>

            {renderStatuses(order)}
            <input
              className="mo-order-comment"
              type="text"
              placeholder="Comment…"
              value={comments[order.orderID] ?? ''}
              onClick={e => e.stopPropagation()}
              onChange={e => setComments(prev => ({ ...prev, [order.orderID]: e.target.value }))}
              onBlur={() => saveComment(order.orderID)}
            />
            <div className="mo-order-amount">
              ${Number(order.total_amount || 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div className="mo-order-arrow">
              <i className="fas fa-chevron-right"></i>
            </div>
          </div>
        ))}
      </div>

      {renderPagination()}

      <div className="mo-totals-bar">
        <span className="mo-totals-item">
          <i className="fas fa-layer-group"></i>
          {total.toLocaleString('en-US')} order{total !== 1 ? 's' : ''}
        </span>
        <span className="mo-totals-sep">·</span>
        <span className="mo-totals-item">
          <i className="fas fa-cubes"></i>
          {Number(totalQty).toLocaleString('en-US', { maximumFractionDigits: 2 })}
          {' '}{(isSeeds || isFertilizer) ? 'lb' : 'pcs'} total

        </span>
        <span className="mo-totals-sep">·</span>
        <span className="mo-totals-item mo-totals-amount">
          <i className="fas fa-dollar-sign"></i>
          ${Number(totalAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total
        </span>
      </div>
    </div>
  );
};

export default MaterialOrdersList;
